import { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Image, Share } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { getGymJoinCode } from '@/lib/gymJoinCode';
import { resolveClubOwnerOrg } from '@/lib/orgProvisioning';

type GymJoinInfo = {
  id: string;
  name?: string | null;
  join_code?: string | null;
};

export default function GymJoinCodeScreen() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [gym, setGym] = useState<GymJoinInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [effectiveGymId, setEffectiveGymId] = useState<string | null>(profile?.gym_id ?? null);

  // Access guard: legacy gym owners OR Club org owners (org_role === 'owner')
  const canView = __DEV__ || profile?.is_gym_owner || (profile as any)?.org_role === 'owner';

  useEffect(() => {
    const loadGym = async () => {
      setLoading(true);
      setError('');

      try {
        // Resolve effectiveGymId: prefer gym_id; fall back to org id for Club org owners
        let gid: string | null = profile?.gym_id ?? null;
        if (!gid && profile?.id) {
          gid = await resolveClubOwnerOrg(profile.id);
        }
        setEffectiveGymId(gid);

        // For Club org owners resolved via organizations, check membership role
        let isOwner = canView;
        if (!isOwner && gid && profile?.id) {
          const { data: mem } = await supabase
            .from('organization_memberships')
            .select('role')
            .eq('org_id', gid)
            .eq('user_id', profile.id)
            .eq('status', 'active')
            .maybeSingle();
          isOwner = mem?.role === 'owner';
        }

        if (!isOwner) {
          setLoading(false);
          return;
        }

        if (!gid) {
          setLoading(false);
          return;
        }

        // Try gyms table first (legacy gym owners)
        const { data, error: joinError } = await supabase
          .from('gyms')
          .select('id, name, join_code')
          .eq('id', gid)
          .maybeSingle();

        const joinCodeMissing =
          joinError?.message?.toLowerCase().includes('join_code') ||
          joinError?.message?.toLowerCase().includes('does not exist');

        if (joinError && !joinCodeMissing) {
          // Could be a Club org — try organizations table
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select('id, name')
            .eq('id', gid)
            .maybeSingle();
          if (orgError) throw orgError;
          if (orgData) {
            setGym({ id: orgData.id, name: orgData.name, join_code: null });
            return;
          }
          throw joinError;
        }

        if (!data) {
          // No gym row — try organizations
          const { data: orgData } = await supabase
            .from('organizations')
            .select('id, name')
            .eq('id', gid)
            .maybeSingle();
          if (orgData) {
            setGym({ id: orgData.id, name: orgData.name, join_code: null });
            return;
          }
          setGym(null);
          return;
        }

        if (data && joinCodeMissing) {
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('gyms')
            .select('id, name')
            .eq('id', gid)
            .maybeSingle();

          if (fallbackError) throw fallbackError;
          setGym(fallbackData as GymJoinInfo);
          return;
        }

        setGym(data as GymJoinInfo);
      } catch (err: any) {
        setError(err?.message || 'Unable to load gym join code.');
      } finally {
        setLoading(false);
      }
    };

    loadGym();
  }, [profile?.gym_id, profile?.id]);

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  if (!canView && !effectiveGymId) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorText, { color: theme.colors.textSecondary }]}>
          Join code is only available to gym owners.
        </Text>
      </View>
    );
  }

  if (!effectiveGymId) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorText, { color: theme.colors.textSecondary }]}>
          No gym selected.
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>
      </View>
    );
  }

  const { code: joinCode } = getGymJoinCode(effectiveGymId, gym?.join_code);
  const joinUrl = `https://douevenlift.com/join/${effectiveGymId}`;
  const gymDisplayName = gym?.name || 'Gym';

  const handleShare = async () => {
    try {
      await Share.share({
        message: `Join ${gymDisplayName} on DUEL: ${joinUrl}`,
        url: joinUrl,
      });
    } catch {
      // dismissed
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
          <Text style={[styles.backText, { color: theme.colors.accent }]}>Back</Text>
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>JOIN CODE</Text>
        <View style={{ width: 50 }} />
      </View>

      <View style={styles.content}>
        <Text style={[styles.title, { color: theme.colors.text }]}>{gymDisplayName}</Text>
        <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
          Share this code or scan to join {gymDisplayName}.
        </Text>

        <Image
          source={{ uri: `https://api.qrserver.com/v1/create-qr-code/?size=300x300&color=000000&bgcolor=ffffff&data=${encodeURIComponent(joinUrl)}` }}
          style={{ width: 220, height: 220, borderWidth: 3, borderColor: theme.colors.accent }}
          resizeMode="contain"
        />

        <Text style={[styles.noteText, { color: theme.colors.textSecondary }]}>
          Scan to join this gym
        </Text>

        <View style={[styles.codeCard, { borderColor: theme.colors.border, backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.codeText, { color: theme.colors.text }]}>{joinCode}</Text>
        </View>

        <TouchableOpacity
          onPress={handleShare}
          style={[styles.shareButton, { backgroundColor: theme.colors.accent }]}
        >
          <Text style={[styles.shareButtonText, { color: theme.colors.background }]}>Share Join Link</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    padding: 24,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingTop: 60,
    paddingBottom: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 50,
  },
  backText: {
    fontSize: 14,
    fontWeight: '700',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 1,
  },
  content: {
    paddingTop: 90,
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: '800',
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
  },
  codeCard: {
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 24,
    paddingHorizontal: 32,
    marginTop: 16,
  },
  codeText: {
    fontSize: 32,
    fontWeight: '900',
    letterSpacing: 4,
  },
  noteText: {
    fontSize: 12,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
  },
  shareButton: {
    borderRadius: 2,
    paddingVertical: 14,
    paddingHorizontal: 32,
    marginTop: 8,
  },
  shareButtonText: {
    fontSize: 15,
    fontWeight: '700',
  },
});
